import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { apiListArticles, ApiErrorResponse, ArticleSummary } from '../api/client';
import PortalPageShell from './PortalPageShell';
import { DisclaimerContent } from '../pages/Disclaimer';
import SubscriptionAccessOverlay from './SubscriptionAccessOverlay';
import useMemberSubscriptionAccess from './useMemberSubscriptionAccess';

export default function ArticlesPage() {
  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    // Show disclaimer once per login session. We use the session start key set at login.
    try {
      const sessionStarted = localStorage.getItem('me_auth_session_started_at') || String(Date.now());
      const shownFor = sessionStorage.getItem('me_disclaimer_shown_for_session');
      if (sessionStarted && shownFor !== sessionStarted) {
        setShowDisclaimer(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const handleAgreeDisclaimer = () => {
    try {
      const sessionStarted = localStorage.getItem('me_auth_session_started_at') || String(Date.now());
      sessionStorage.setItem('me_disclaimer_shown_for_session', sessionStarted);
    } catch (e) {}
    setShowDisclaimer(false);
  };

  const handleDeclineDisclaimer = () => {
    // If user declines, navigate them away from member area
    setShowDisclaimer(false);
    window.location.href = '/';
  };
  const {
    hasArticleAccess,
    isExpired,
    latestPayment,
    loadingSubscription,
    subscription,
    subscriptionError,
  } = useMemberSubscriptionAccess();

  useEffect(() => {
    apiListArticles().then((data: ArticleSummary[] | ApiErrorResponse) => {
      if (Array.isArray(data)) {
        setArticles(data);
      } else {
        setArticles([]);
      }
      setLoading(false);
    });
  }, []);

  const filteredArticles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return articles;

    return articles.filter(article => {
      const title = article.title?.toLowerCase() || '';
      const excerpt = article.excerpt?.toLowerCase() || '';
      return title.includes(term) || excerpt.includes(term);
    });
  }, [articles, search]);

  const formatDateTime = (value?: string) => {
    if (!value) return '';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  if (loading || loadingSubscription) {
    return (
      <PortalPageShell title="Member Articles" subtitle="Read articles and premium resources.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading articles...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  const content = (
    <Stack spacing={6}>
      <Box
        position="relative"
        minH={{ base: '180px', md: '220px' }}
        borderRadius="2xl"
        overflow="hidden"
        backgroundImage="url('/background/patrick-baum-oQTl_mHd2Ik-unsplash.jpg')"
        backgroundSize="cover"
        backgroundPosition="center center"
        backgroundRepeat="no-repeat"
      >
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-r, rgba(15,23,42,0.72), rgba(15,23,42,0.28))"
        />
        <Stack
          position="relative"
          zIndex={1}
          spacing={3}
          justify="flex-end"
          h="full"
          px={{ base: 6, md: 8 }}
          py={{ base: 6, md: 8 }}
        >
          <Text
            color="white"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="700"
          >
            Research & Insights
          </Text>
          <Text color="gray.100" maxW="2xl">
            Explore articles all in one place.
          </Text>
        </Stack>
      </Box>
      <Input
        placeholder="Search articles by title or summary"
        value={search}
        onChange={e => setSearch(e.target.value)}
        maxW="400px"
      />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {filteredArticles.map(article => (
          <Stack
            key={article.id}
            spacing={3}
            p={5}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            bg="gray.50"
          >
            <Badge width="fit-content" colorScheme="blue">
              Article
            </Badge>
            <Link as={RouterLink} to={`/member/articles/${article.id}`} fontWeight="700" fontSize="lg" color="blue.700">
              {article.title}
            </Link>
            <Text color="gray.600">{article.excerpt || 'Open this article to view the full content.'}</Text>
            {article.published_at ? (
              <Text color="gray.500" fontSize="sm">
                Published at: {formatDateTime(article.published_at)}
              </Text>
            ) : null}
          </Stack>
        ))}
      </SimpleGrid>
      {filteredArticles.length === 0 ? (
        <Text color="gray.600">No articles match your search.</Text>
      ) : null}
    </Stack>
  );

  return (
    <PortalPageShell title="Member Articles" subtitle="Read articles and portal content.">
      {/* Disclaimer Modal (shown once per login/session) */}
      <Modal isOpen={showDisclaimer} onClose={() => {}} isCentered closeOnEsc={false} closeOnOverlayClick={false} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Disclaimer & Agreement</ModalHeader>
          <ModalBody maxH="60vh" overflowY="auto">
            <DisclaimerContent />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleDeclineDisclaimer}>Decline</Button>
            <Button colorScheme="blue" onClick={handleAgreeDisclaimer}>Agree</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {hasArticleAccess ? content : (
        <SubscriptionAccessOverlay
          subscription={subscription}
          latestPayment={latestPayment}
          isExpired={isExpired}
          subscriptionError={subscriptionError}
        >
          {content}
        </SubscriptionAccessOverlay>
      )}
    </PortalPageShell>
  );
}
